import React, { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Table, Spinner } from 'react-bootstrap';
import { useColumnManagement } from './useColumnManagement';
import { DataTableProps } from './types';

/**
 * Generic DataTable component with advanced features
 *
 * Features:
 * - Column reordering via drag and drop
 * - Column resizing with min-width constraints
 * - Column visibility toggling via context menu
 * - Sticky header that stays visible during scroll
 * - Infinite scroll support with intersection observer
 * - Sortable columns with visual indicators
 * - Row click handlers
 * - Row highlighting
 * - User preference persistence
 * - Loading states
 * - Fully type-safe with TypeScript generics
 *
 * @example
 * ```tsx
 * import { DataTable } from './components/common/DataTable';
 * import type { ColumnConfig } from './components/common/DataTable/types';
 *
 * interface MyData {
 *   id: string;
 *   name: string;
 *   email: string;
 * }
 *
 * const columns: ColumnConfig<MyData>[] = [
 *   {
 *     key: 'name',
 *     label: 'Name',
 *     sortable: true,
 *     width: 200,
 *     renderCell: (item) => <div className="fw-semibold">{item.name}</div>
 *   },
 *   {
 *     key: 'email',
 *     label: 'Email',
 *     sortable: true,
 *     width: 250,
 *     renderCell: (item) => <a href={`mailto:${item.email}`}>{item.email}</a>
 *   }
 * ];
 *
 * function MyTable() {
 *   const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });
 *
 *   return (
 *     <DataTable
 *       data={myData}
 *       columns={columns}
 *       getRowKey={(item) => item.id}
 *       sortConfig={sortConfig}
 *       onSortChange={(field, direction) => setSortConfig({ field, direction })}
 *       preferencesKey="my-table"
 *       maxHeight="calc(100vh - 200px)"
 *       enableColumnReorder
 *       enableColumnResize
 *       enableColumnVisibility
 *       enableStickyHeader
 *     />
 *   );
 * }
 * ```
 */
export function DataTable<T extends Record<string, any>>({
  // Data
  data,
  columns: defaultColumns,
  getRowKey,

  // Pagination
  pagination,
  onLoadMore,
  isLoading = false,

  // Sorting
  sortConfig,
  onSortChange,

  // Row interactions
  onRowClick,
  highlightedRows,
  modifiedCells,

  // Preferences
  preferencesKey,
  userId,
  userPreferences,

  // Styling
  className = '',
  maxHeight = '600px',

  // Empty state
  emptyState,

  // Features
  enableColumnReorder = true,
  enableColumnResize = true,
  enableColumnVisibility = true,
  enableStickyHeader = true,
}: DataTableProps<T>): React.ReactElement {
  // ========== State ==========
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // ========== Refs ==========
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  // Sentinel is tracked as STATE (set via a callback ref), not a plain ref.
  // The observer effect lists it as a dependency so it re-runs the instant the
  // sentinel node mounts — eliminating the mount-timing race where the effect
  // fired while sentinelRef.current was still null and, because it was keyed
  // only on hasMore, never retried (leaving the observer unattached and page 2
  // never requested — an intermittent, reload-sensitive "stuck at page 1").
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);

  // ========== Column Management ==========
  const {
    columnOrder,
    resize,
    drag,
    toggleColumnVisibility,
    resetToDefaults,
    justResized,
  } = useColumnManagement({
    defaultColumns: defaultColumns.map((col) => ({
      ...col,
      sortable: col.sortable ?? false,
      width: col.width ?? 150,
      visible: col.visible ?? true,
      resizable: col.resizable ?? true,
    })),
    preferencesKey,
    userId,
    userPreferences,
    enableColumnReorder,
  });

  // ========== Infinite Scroll Setup ==========
  //
  // isLoading and onLoadMore are read out of refs inside the callbacks so they
  // don't appear in effect deps — including them recreated the observer every
  // time loading toggled, and a freshly-attached IntersectionObserver fires its
  // initial-state callback on the next tick. After a page landed
  // (isLoading: true → false) the new observer would report isIntersecting=true
  // on the still-not-yet-reflowed sentinel and fire onLoadMore again,
  // double-paging on every scroll trigger. The observer stays alive across
  // loading state so it only fires on real intersection transitions.
  //
  // onLoadMore (handleLoadMore in usePaginatedQuery) is idempotent — it guards
  // on its own in-flight flag and on the cursor not having advanced — so it is
  // safe to drive from BOTH the observer (covers auto-fill when page 1 doesn't
  // fill the container) and the scroll fallback below (covers the user
  // scrolling to the bottom). Two independent triggers means a single one
  // misfiring can no longer strand the list at page 1.
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  // Observer: re-runs when the sentinel node mounts (sentinelEl dep) so the
  // attach can't lose a race against ref population. root is read here, after
  // commit, so tableWrapperRef.current is reliably set.
  React.useEffect(() => {
    if (!pagination?.hasMore || !sentinelEl) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Load more when sentinel becomes visible and we're not already loading
        if (entries[0].isIntersecting && !isLoadingRef.current && onLoadMoreRef.current) {
          onLoadMoreRef.current();
        }
      },
      {
        threshold: 0.1,
        root: tableWrapperRef.current, // Use scroll container as root, not viewport
      }
    );

    observer.observe(sentinelEl);

    return () => observer.disconnect();
  }, [pagination?.hasMore, sentinelEl]);

  // Scroll fallback: an IntersectionObserver can fail to fire (mount-timing
  // races, a sentinel that never crosses the threshold, browser quirks). A
  // plain scroll listener on the container guarantees the next page is
  // requested once the user scrolls near the bottom — the exact action that
  // was previously getting stuck.
  React.useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el || !pagination?.hasMore) {
      return;
    }

    const onScroll = () => {
      if (isLoadingRef.current || !onLoadMoreRef.current) {
        return;
      }
      // within 200px of the bottom
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        onLoadMoreRef.current();
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [pagination?.hasMore]);

  // ========== Context Menu Handlers ==========
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      e.preventDefault();

      // Calculate position, accounting for viewport boundaries
      const menuWidth = 200; // minWidth from menu style
      const menuHeight = 400; // maxHeight from menu style
      const padding = 10; // padding from viewport edge

      let x = e.clientX;
      let y = e.clientY;

      // If menu would go off right edge, open to the left of cursor
      if (x + menuWidth > window.innerWidth - padding) {
        x = window.innerWidth - menuWidth - padding;
      }

      // If menu would go off bottom edge, move it up
      if (y + menuHeight > window.innerHeight - padding) {
        y = window.innerHeight - menuHeight - padding;
      }

      // Clamp to top of viewport so it's never above the screen
      if (y < padding) {
        y = padding;
      }

      setContextMenu({ x, y });
    },
    []
  );

  // Close context menu when clicking elsewhere or resizing
  React.useEffect(() => {
    const handleClick = () => setContextMenu(null);
    const handleResize = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      window.addEventListener('resize', handleResize);
      return () => {
        document.removeEventListener('click', handleClick);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [contextMenu]);

  // ========== Sort Handlers ==========
  const handleSort = useCallback(
    (columnKey: string) => {
      // Don't sort if we just resized (prevents accidental sort on resize)
      if (justResized.current) {
        return;
      }

      if (!onSortChange) return;

      const column = columnOrder.find((col) => col.key === columnKey);
      if (!column?.sortable) return;

      // Toggle sort direction or set to asc if sorting a new field
      const newDirection =
        sortConfig?.field === columnKey && sortConfig.direction === 'asc'
          ? 'desc'
          : 'asc';

      onSortChange(columnKey, newDirection);
    },
    [columnOrder, sortConfig, onSortChange, justResized]
  );

  // ========== Helper Functions ==========
  const getSortIcon = useCallback(
    (columnKey: string): string => {
      if (sortConfig?.field !== columnKey) {
        return 'bi-arrow-down-up';
      }
      return sortConfig.direction === 'asc'
        ? 'bi-arrow-up'
        : 'bi-arrow-down';
    },
    [sortConfig]
  );

  // Get column style for fixed width - not memoized since column width changes during resize
  const getColumnStyle = (column: (typeof columnOrder)[0]): React.CSSProperties => {
    return {
      width: `${column.width}px`,
      minWidth: `${column.width}px`,
      maxWidth: `${column.width}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
  };

  // ========== Client-side sorting (only when no pagination/server-side sorting) ==========
  const sortedData = useMemo(() => {
    if (!sortConfig?.field || pagination) return data;
    const col = defaultColumns.find(c => c.key === sortConfig.field);
    if (!col?.sortable) return data;

    return [...data].sort((a, b) => {
      const aVal = col.sortValue ? col.sortValue(a) : (a as any)[col.key];
      const bVal = col.sortValue ? col.sortValue(b) : (b as any)[col.key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let cmp: number;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      }

      return sortConfig.direction === 'desc' ? -cmp : cmp;
    });
  }, [data, sortConfig, defaultColumns, pagination]);

  // Debug: log horizontal overflow
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (el.scrollWidth > el.clientWidth) {
        console.warn(`[DataTable] horizontal overflow: scrollWidth=${el.scrollWidth} clientWidth=${el.clientWidth} overflow=${el.scrollWidth - el.clientWidth}px`);
      }
    });
  }, [sortedData, columnOrder]);

  // ========== Render ==========
  const showEmptyOverlay = !isLoading && data.length === 0;

  return (
    <>
      <div style={{ position: 'relative', height: maxHeight === '100%' ? '100%' : undefined, flex: maxHeight === '100%' ? '1 1 0' : undefined }}>
      <div
        ref={tableWrapperRef}
        className={`data-table-wrapper ${className}`}
        style={{
          position: 'relative',
          overflow: 'auto',
          // Use height when maxHeight is percentage-based (requires explicit parent height),
          // otherwise use maxHeight for absolute values like calc(100vh - Npx)
          ...(maxHeight === '100%'
            ? { height: '100%' }
            : { maxHeight }),
        }}
      >
      <style>{`
        /* Sticky table header */
        ${
          enableStickyHeader
            ? `
        .data-table-wrapper .table thead th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background-color: var(--bs-secondary-bg) !important;
          word-wrap: break-word !important;
          white-space: normal !important;
        }

        /* Add borders as pseudo-elements to ensure they're part of the sticky element */
        .data-table-wrapper .table thead th::before {
          content: '' !important;
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          bottom: 0 !important;
          width: 1px !important;
          background-color: var(--bs-border-color) !important;
          z-index: 1 !important;
        }

        .data-table-wrapper .table thead th::after {
          content: '' !important;
          position: absolute !important;
          right: 0 !important;
          top: 0 !important;
          bottom: 0 !important;
          width: 1px !important;
          background-color: var(--bs-border-color) !important;
          z-index: 1 !important;
        }
        `
            : ''
        }

        /* Column drag and drop styling */
        .data-table-wrapper .column-dragging {
          background-color: var(--app-drag-bg) !important;
        }

        /* Resize handle */
        .data-table-wrapper .resize-handle {
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          cursor: ew-resize;
          user-select: none;
          z-index: 10;
          background-color: transparent;
        }

        .data-table-wrapper .resize-handle:hover {
          background-color: var(--app-resize-handle) !important;
        }

        /* Row click cursor */
        .data-table-wrapper tbody tr.clickable-row {
          cursor: pointer;
        }

        .data-table-wrapper tbody tr.clickable-row:hover {
          background-color: var(--app-editable-cell) !important;
        }

        /* Highlighted/Modified row */
        .data-table-wrapper tbody tr.highlighted-row {
          background-color: var(--app-modified-cell) !important;
        }

        .data-table-wrapper tbody tr.highlighted-row > td {
          background-color: var(--app-modified-cell) !important;
        }

        .data-table-wrapper tbody tr.highlighted-row > td.modified-cell,
        .data-table-wrapper tbody td.modified-cell {
          background-color: var(--app-modified-cell-changed) !important;
        }

        /* Editable cell styling */
        .data-table-wrapper td.editable-cell {
          background-color: var(--app-editable-cell) !important;
          position: relative;
        }

        .data-table-wrapper td.editable-cell:hover {
          background-color: var(--app-editable-cell-hover) !important;
        }

        /* Editable column header styling */
        .data-table-wrapper th.editable-header {
          background-color: var(--app-editable-cell) !important;
        }

        /* Override for modified cells that are also editable */
        .data-table-wrapper tbody tr.highlighted-row > td.editable-cell.modified-cell {
          background-color: var(--app-modified-cell-changed) !important;
        }

        .data-table-wrapper tbody tr.highlighted-row > td.editable-cell.modified-cell:hover {
          background-color: var(--app-modified-cell-changed) !important;
        }
      `}</style>

      {/* Loading overlay for initial load */}
      {isLoading && data.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'var(--bs-body-bg)',
            zIndex: 10,
          }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}


      <Table
        hover
        striped
        className="mb-0"
        style={{ whiteSpace: 'nowrap', position: 'relative' }}
      >
        {/* Table Header */}
        <thead onContextMenu={handleContextMenu}>
          <tr>
            {columnOrder.filter((col) => col.visible).map((column, visibleIndex) => {
              // Get actual column index for this visible column
              const actualColumnIndex = columnOrder.findIndex(
                (col) => col.key === column.key
              );

              return (
                <th
                  key={column.key}
                  ref={(el) => {
                    if (el) {
                      resize.headerRefs.current.set(actualColumnIndex, el);
                    } else {
                      resize.headerRefs.current.delete(actualColumnIndex);
                    }
                  }}
                  className={`user-select-none ${
                    column.sortable ? 'cursor-pointer' : ''
                  } ${column.editable ? 'editable-header' : ''}`}
                  draggable={enableColumnReorder}
                  onDragStart={
                    enableColumnReorder
                      ? (e) => drag.handleDragStart(e, visibleIndex)
                      : undefined
                  }
                  onDragOver={
                    enableColumnReorder ? drag.handleDragOver : undefined
                  }
                  onDrop={
                    enableColumnReorder
                      ? (e) => drag.handleDrop(e, visibleIndex)
                      : undefined
                  }
                  onClick={
                    column.headerOnClick
                      ? column.headerOnClick
                      : column.sortable
                        ? () => handleSort(column.key)
                        : undefined
                  }
                  onMouseEnter={(e) => {
                    const resizeHandle = e.currentTarget.querySelector(
                      '.resize-handle'
                    ) as HTMLElement;
                    if (resizeHandle) resizeHandle.style.backgroundColor = 'var(--app-resize-handle)';
                  }}
                  onMouseLeave={(e) => {
                    const resizeHandle = e.currentTarget.querySelector(
                      '.resize-handle'
                    ) as HTMLElement;
                    if (resizeHandle) resizeHandle.style.backgroundColor = 'transparent';
                  }}
                  style={{
                    ...getColumnStyle(column),
                    cursor: column.headerOnClick
                      ? 'pointer'
                      : enableColumnReorder
                        ? 'move'
                        : 'default',
                    backgroundColor:
                      drag.draggedColumn === visibleIndex ? 'var(--app-drag-bg)' : undefined,
                    position: 'relative',
                    verticalAlign: 'middle',
                  }}
                  title={
                    enableColumnReorder
                      ? `Drag to reorder ${column.label}`
                      : column.label
                  }
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ height: '100%' }}
                  >
                    {enableColumnReorder && (
                      <i
                        className="bi bi-grip-vertical text-muted"
                        style={{ fontSize: '0.7rem', cursor: 'move' }}
                      ></i>
                    )}
                    {column.renderHeader ? column.renderHeader(column) : <span>{column.label}</span>}
                    {column.sortable && (
                      <i
                        className={`bi ${getSortIcon(column.key)} ms-1`}
                        style={{ cursor: 'pointer' }}
                      ></i>
                    )}
                  </div>
                  {enableColumnResize && column.resizable && (
                    <div
                      className="resize-handle"
                      onMouseDown={(e) => resize.handleResizeStart(e, visibleIndex)}
                      style={{
                        display:
                          resize.resizingColumn !== null &&
                          resize.resizingColumn !== actualColumnIndex
                            ? 'none'
                            : 'block',
                      }}
                      title="Drag to resize column"
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        {/* Table Body */}
        <tbody>
          {sortedData.map((item) => {
            const rowKey = getRowKey(item);
            const isHighlighted = highlightedRows?.has(rowKey);
            const isClickable = !!onRowClick;

            return (
              <tr
                key={rowKey}
                className={`${isHighlighted ? 'highlighted-row' : ''} ${
                  isClickable ? 'clickable-row' : ''
                }`}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columnOrder.filter((col) => col.visible).map((column) => {
                  const cellStyle = getColumnStyle(column);

                  // Get cell content
                  let cellContent: React.ReactNode;
                  if (column.renderCell) {
                    cellContent = column.renderCell(item, column);
                  } else {
                    // Default rendering: just display the value
                    cellContent = item[column.key];
                  }

                  // Get tooltip text
                  let tooltipText: string | undefined;
                  if (column.tooltip) {
                    tooltipText = column.tooltip(item);
                  } else if (typeof cellContent === 'string') {
                    tooltipText = cellContent;
                  }

                  // Check if this cell is modified
                  const isCellModified = modifiedCells?.get(rowKey)?.has(column.key) ?? false;

                  // Build cell class names
                  const cellClassNames = [];
                  if (column.editable) {
                    cellClassNames.push('editable-cell');
                  }
                  if (isCellModified) {
                    cellClassNames.push('modified-cell');
                  }

                  return (
                    <td
                      key={`${rowKey}-${column.key}`}
                      style={cellStyle}
                      title={tooltipText}
                      className={cellClassNames.join(' ')}
                      onClick={column.onCellClick ? () => column.onCellClick!(item) : undefined}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Infinite scroll sentinel */}
      {pagination?.hasMore && (
        <div
          ref={setSentinelEl}
          className="d-flex justify-content-center p-3"
          style={{ minHeight: '20px' }}
        >
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <div style={{ height: '20px' }} />
          )}
        </div>
      )}
      </div>

      {/* Empty state overlay - outside scroll container so it doesn't move with horizontal scroll */}
      {showEmptyOverlay && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <div style={{ pointerEvents: 'auto' }}>
            {emptyState || (
              <div className="text-center py-5">
                <i className="bi bi-inbox fs-1 text-muted mb-3 d-block"></i>
                <h5 className="text-muted">No Data</h5>
                <p className="text-muted mb-0">No records found.</p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>

      {/* Context Menu - rendered via portal directly to document.body */}
      {enableColumnVisibility && contextMenu && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: 'var(--bs-body-bg)',
            border: '1px solid var(--bs-border-color)',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1060,
            minWidth: '200px',
            maxHeight: `${Math.min(400, window.innerHeight - contextMenu.y - 10)}px`,
            overflowY: 'auto',
          }}
        >
          <div className="p-2">
            <div
              className="px-2 py-1 cursor-pointer text-danger"
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              onClick={resetToDefaults}
            >
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset to Defaults
            </div>
            <div className="small text-muted px-2 py-1" style={{ fontSize: '0.75rem' }}>
              Resets column order, width, visibility, and sort
            </div>
            <hr className="my-2" />
            <div className="fw-bold text-muted small px-2 py-1">Column Visibility</div>
            {columnOrder.map((column) => (
              <div
                key={column.key}
                className="px-2 py-1 cursor-pointer"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                onClick={() => toggleColumnVisibility(column.key)}
              >
                <i className={`bi ${column.visible ? 'bi-check-square' : 'bi-square'} me-2`}></i>
                {column.label}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default DataTable;
